import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'Data/Store/Store';
import type {
  CartState,
  ICartItemData,
  AddToCartPayload,
  CartItem,
  IUserCartStore,
  MapCartStateToICartItemsFn,
  ComputeCartSubtotalFromICartItemsFn,
} from '@model/Cart.model';
// import { mapCartStateToICartItems,computeCartSubtotalFromICartItems,} from "Logic/CartService";
import { addToCartAction } from './AddToCartAction';
import { removeFromCartAction } from './RemoveFromCartAction';
import { setCartItemQuantityAction } from './SetCartItemQuantityAction';
import { clearCartAction } from './ClearCartAction';
import { setCartAllAction } from './SetCartAllAction';
import { createSelector } from '@reduxjs/toolkit';
import { removeManyFromCartAction } from './RemoveManyFromCartAction';

const userCartSelector = (state: RootState) => state.cartReducer;

export const UserCartStoreImplementation = (
  currency = 'USD',
  mapCartStateToICartItems: MapCartStateToICartItemsFn,
  computeCartSubtotalFromICartItems: ComputeCartSubtotalFromICartItemsFn
): IUserCartStore => {
  const dispatch = useDispatch();
  const cartState = useSelector<RootState, CartState | null>(userCartSelector);

  // const itemsICart = useSelector<RootState, ICartItemData[]>((state) =>
  //   mapCartStateToICartItems(state, currency)
  // );

  const makeItemsInCartSelector = (
    mapCartStateToICartItems: MapCartStateToICartItemsFn,
    currency: string
  ) =>
    createSelector([(state: RootState) => state, () => currency], (state, currency) =>
      mapCartStateToICartItems(state, currency)
    );

  const itemsICartSelector = useMemo(
    () => makeItemsInCartSelector(mapCartStateToICartItems, currency),
    [mapCartStateToICartItems, currency]
  );

  const itemsICart = useSelector(itemsICartSelector);

  const subtotal = useMemo(() => computeCartSubtotalFromICartItems(itemsICart), [itemsICart]);
  const totalQuantity = useMemo(
    () => itemsICart.reduce((sum, it) => sum + Number(it.qty || 0), 0),
    [itemsICart]
  );
  const isCartEmpty = itemsICart.length === 0;

  const addItemToCart = useCallback(
    (payload: AddToCartPayload) => addToCartAction(payload)(dispatch),
    [dispatch]
  );

  const removeManyFromCart = useCallback(
    (ids: string[]) => removeManyFromCartAction({ ids })(dispatch),
    [dispatch]
  );

  const removeItemFromCart = useCallback(
    (productId: string) => removeFromCartAction({ id: productId })(dispatch),
    [dispatch]
  );

  const setCartItemQuantity = useCallback(
    (productId: string, quantity: number) =>
      setCartItemQuantityAction({
        id: productId,
        quantity: Math.max(0, Math.floor(quantity || 0)),
      })(dispatch),
    [dispatch]
  );

  const clearCart = useCallback(() => clearCartAction()(dispatch), [dispatch]);

  const replaceCartWith = useCallback(
    (items?: CartItem[]) => setCartAllAction(items)(dispatch),
    [dispatch]
  );

  return {
    cartState,
    itemsICart,
    subtotal,
    totalQuantity,
    isCartEmpty,
    currency,
    addItemToCart,
    removeItemFromCart,
    setCartItemQuantity,
    removeManyFromCart,
    clearCart,
    replaceCartWith,
  };
};
